// ui/src/components/tenant-admin/CalculationRunMonitor.jsx
/**
 * @file CalculationRunMonitor.jsx
 * @module ui/src/components/tenant-admin/CalculationRunMonitor
 * @description
 *   Polls a selected calculation run every 3 seconds until it reaches a terminal
 *   status (completed or failed). Lists available runs for the fiscal period and
 *   allows manual run ID entry. On failure, emits an AI status prompt to parent.
 *   On completion, dispatches decisionflow:navigate to the payout results screen.
 *
 * @author data-fit LLC · DecisionFlow · S776
 * @see decisionflow_api_ui_master_map_s776.md
 *
 * @api
 *   GET /api/v1/calculation-runs — list runs (outside v4 ep 1–61 scope).
 *     OI-EXEC-CALCULATION-RUNS-LIST-PARAMS: query params not confirmed in v1.11.
 *     Response fallback: payload.data[] | payload.calculation_runs[] | payload[].
 *   GET /api/v1/calculation-runs/{id} — single run poll (outside v4 ep 1–61 scope).
 *     Distinct from GET /api/v1/jobs/{jobId} (ep 55). Response: payload.data | payload.
 *   NOTE: Neither endpoint is in the master map. Header-only pass — no wiring change.
 *
 * @architecture
 *   DDL     : R5 · API: v1.11 · Session 92
 *   Auth    : useAuth() direct — ARCH-2 compliant
 *   Patches : PATCH-S92-08 (run id: calculation_run_id || id);
 *             PATCH-S212 (fiscalPeriodId destructured; client-side filter §13.3 stub)
 *             M040 C2 (colors → tokens)
 *
 * @open-items
 *   OI-EXEC-CALCULATION-RUNS-LIST-PARAMS — GET /api/v1/calculation-runs list params unconfirmed
 */

// data-fit LLC · DecisionFlow · EXECUTE Stage
// CalculationRunMonitor.jsx · DDL: R5 · API: v1.11 · Session 92
// Auth: useAuth() direct — ARCH-2 compliant
// Key constraints: 3-second polling, terminal status cleanup, run_status read-only
// PATCH-S92-08: run id read as calculation_run_id || id (DDL PK: calculation_run_id)
// PATCH-S212: fiscalPeriodId destructured; client-side filter by fiscal_period_id + run_type + run_status (§13.3 contract stub)
// M040 C2: dark: prefix removed, hardcoded colors → tokens, text-* → text-tok-*, all <td> get text-[rgb(var(--color-text))]

import React, { useEffect, useMemo, useState } from 'react'
import { useAuth } from '../../auth/AuthContext.jsx'
import { SCREEN_ORDER } from './executeFlowConstants'

const TERMINAL_STATUSES = ['completed', 'failed']
const RUN_STATUSES = ['pending', 'running', 'completed', 'failed']

function getErrorMessage(payload) {
  const error = payload?.error
  if (!error) return 'Unexpected calculation monitor error.'

  if (error.code === 'CALCULATION_RUN_NOT_FOUND') {
    return 'Calculation run was not found.'
  }

  if (error.code === 'AUTH_INSUFFICIENT_ROLE') {
    return 'You do not have the comp_admin role required to monitor runs.'
  }

  return error.recovery_action || error.message || 'Unable to monitor run.'
}

export default function CalculationRunMonitor({
  fiscalPeriodId,
  runId,
  setRunId,
  aiStatus,
  setAiStatus,
  setAiResponse,
}) {
  const { token } = useAuth()
  const normalizedPeriodId = fiscalPeriodId != null ? Number(fiscalPeriodId) : null

  const [manualRunId, setManualRunId] = useState(runId || '')
  const [run, setRun] = useState(null)
  const [runs, setRuns] = useState([])
  const [loading, setLoading] = useState(false)
  const [listLoading, setListLoading] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')
  const [lastPolledAt, setLastPolledAt] = useState(null)

  const activeRunId = useMemo(
    () => runId || manualRunId.trim(),
    [runId, manualRunId],
  )

  const loadRunList = async () => {
    setListLoading(true)

    try {
      // OI-EXEC-CALCULATION-RUNS-LIST-PARAMS: GET /api/v1/calculation-runs list params not confirmed in v1.11
      const response = await fetch('/api/v1/calculation-runs', {
        credentials: 'include',
      })

      const payload = await response.json().catch(() => ({}))

      if (!response.ok) {
        throw new Error(getErrorMessage(payload))
      }

      const allRuns = Array.isArray(payload?.data)
        ? payload.data
        : Array.isArray(payload?.calculation_runs)
          ? payload.calculation_runs
          : Array.isArray(payload)
            ? payload
            : []
      // §13.3 contract stub — filter client-side by fiscal_period_id only.
      // All run types and statuses shown so operators can monitor in-progress
      // and preview runs, not just completed finals. S1000 item 4a fix.
      const nextRuns = normalizedPeriodId
        ? allRuns.filter((r) => r.fiscal_period_id === normalizedPeriodId)
        : allRuns

      setRuns(nextRuns)
    } catch (error) {
      setErrorMessage(error.message || 'Unable to load calculation runs.')
    } finally {
      setListLoading(false)
    }
  }

  const loadRun = async (id) => {
    if (!id) return

    setLoading(true)
    setErrorMessage('')

    try {
      const response = await fetch(`/api/v1/calculation-runs/${id}`, {
        credentials: 'include',
      })

      const payload = await response.json().catch(() => ({}))

      if (!response.ok) {
        throw new Error(getErrorMessage(payload))
      }

      const nextRun = payload?.data || payload
      setRun(nextRun)
      setLastPolledAt(new Date())
    } catch (error) {
      setErrorMessage(error.message || 'Unable to load calculation run.')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadRunList()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fiscalPeriodId, normalizedPeriodId])

  useEffect(() => {
    if (!activeRunId) return

    let intervalId

    const poll = async () => {
      await loadRun(activeRunId)
    }

    poll()

    intervalId = window.setInterval(() => {
      setRun((currentRun) => {
        if (TERMINAL_STATUSES.includes(currentRun?.run_status)) {
          window.clearInterval(intervalId)
          return currentRun
        }

        loadRun(activeRunId)
        return currentRun
      })
    }, 3000)

    return () => {
      window.clearInterval(intervalId)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeRunId])

  useEffect(() => {
    if (!run) return
    if (aiStatus !== 'idle') return

    if (run.run_status === 'failed') {
      setAiStatus('ready')
      setAiResponse({
        panel_context: 'execute.calculation_run_monitor',
        prompt:
          'Calculation run failed. Review engine diagnostics and retry after resolving blockers.',
      })
    }
  }, [run, aiStatus, setAiStatus, setAiResponse])

  const selectRun = (id) => {
    setRunId(id)
    setManualRunId(id)
  }

  const goNext = () => {
    window.dispatchEvent(
      new CustomEvent('decisionflow:navigate', {
        detail: { screen: SCREEN_ORDER[3] },
      }),
    )
  }

  const status = RUN_STATUSES.includes(run?.run_status)
    ? run.run_status
    : 'pending'

  const isTerminal = TERMINAL_STATUSES.includes(status)

  return (
    <section className="space-y-6">
      <header>
        <p className="text-tok-xs font-semibold uppercase tracking-widest text-[rgb(var(--color-primary))] mb-1">EXEC-03</p>
        <h2 className="text-tok-2xl font-bold text-[rgb(var(--color-text))]">Calculation Run Monitor</h2>
        <p className="text-tok-sm text-[rgb(var(--color-text-muted))] mt-1">
          Polls the selected calculation run every 3 seconds until completed or failed.
        </p>
      </header>

      {errorMessage && (
        <div className="rounded-md bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 text-[rgb(var(--color-danger))] px-4 py-3 text-tok-sm">{errorMessage}</div>
      )}

      <div className="bg-[rgb(var(--color-surface))] border border-[rgb(var(--color-border))] rounded-lg p-6 flex flex-col gap-4 sm:flex-row sm:items-end">
        <label className="flex-1 block text-tok-sm font-medium text-[rgb(var(--color-text))]">
          Calculation Run ID
          <input
            value={manualRunId}
            onChange={(event) => {
              setManualRunId(event.target.value)
              setRunId(null)
            }}
            placeholder="Enter run id"
            className="mt-1 block w-full rounded border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] text-[rgb(var(--color-text))] px-3 py-2 text-tok-sm focus:outline-none"
          />
        </label>
        <button
          type="button"
          disabled={!manualRunId.trim() || loading}
          onClick={() => selectRun(manualRunId.trim())}
          className="px-4 py-2 rounded bg-[rgb(var(--color-primary))] text-white text-tok-sm font-medium hover:bg-[rgb(var(--color-primary-hover))] disabled:opacity-50"
        >
          Monitor run
        </button>
      </div>

      <section className="bg-[rgb(var(--color-surface))] border border-[rgb(var(--color-border))] rounded-lg p-6">
        <h3 className="text-tok-base font-semibold text-[rgb(var(--color-text))] mb-4">Available Runs</h3>
        {listLoading && <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">Loading runs…</p>}
        {!listLoading && runs.length === 0 && <p className="text-tok-sm text-[rgb(var(--color-text-muted))] italic">No runs returned.</p>}
        {!listLoading && runs.length > 0 && (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-[rgb(var(--color-border))] text-tok-sm">
              <thead className="bg-[rgb(var(--color-surface-muted))]">
                <tr>
                  <th className="px-4 py-2 text-left text-tok-xs font-medium text-[rgb(var(--color-text-muted))] uppercase tracking-wider">Run ID</th>
                  <th className="px-4 py-2 text-left text-tok-xs font-medium text-[rgb(var(--color-text-muted))] uppercase tracking-wider">Fiscal Period</th>
                  <th className="px-4 py-2 text-left text-tok-xs font-medium text-[rgb(var(--color-text-muted))] uppercase tracking-wider">Label</th>
                  <th className="px-4 py-2 text-left text-tok-xs font-medium text-[rgb(var(--color-text-muted))] uppercase tracking-wider">Type</th>
                  <th className="px-4 py-2 text-left text-tok-xs font-medium text-[rgb(var(--color-text-muted))] uppercase tracking-wider">Status</th>
                  <th className="px-4 py-2 text-left text-tok-xs font-medium text-[rgb(var(--color-text-muted))] uppercase tracking-wider">Locked</th>
                  <th className="px-4 py-2 text-left text-tok-xs font-medium text-[rgb(var(--color-text-muted))] uppercase tracking-wider">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[rgb(var(--color-border))]">
                {runs.map((item) => {
                  // PATCH-S92-08: DDL PK is calculation_run_id
                  const itemId = item.calculation_run_id || item.id
                  return (
                    <tr key={itemId} className="hover:bg-[rgb(var(--color-surface-muted))]">
                      <td className="px-4 py-2 font-mono text-tok-xs text-[rgb(var(--color-text))]">{itemId}</td>
                      <td className="px-4 py-2 text-[rgb(var(--color-text))]">{item.fiscal_period_id ?? '—'}</td>
                      <td className="px-4 py-2 text-tok-sm text-[rgb(var(--color-text))]">{item.run_label || <span className="text-[rgb(var(--color-text-muted))] italic">—</span>}</td>
                      <td className="px-4 py-2 text-[rgb(var(--color-text))]">{item.run_type}</td>
                      <td className="px-4 py-2 text-[rgb(var(--color-text))]">{item.run_status}</td>
                      <td className="px-4 py-2 text-[rgb(var(--color-text))]">{item.is_locked ? 'Yes' : 'No'}</td>
                      <td className="px-4 py-2">
                        <button type="button" onClick={() => selectRun(itemId)} className="px-3 py-1 rounded border border-[rgb(var(--color-border))] text-[rgb(var(--color-text))] text-tok-xs font-medium hover:bg-[rgb(var(--color-surface-muted))]">
                          Select
                        </button>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section className="bg-[rgb(var(--color-surface))] border border-[rgb(var(--color-border))] rounded-lg p-6" aria-live="polite">
        {!run && loading && <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">Loading selected run…</p>}
        {!run && !loading && <p className="text-tok-sm text-[rgb(var(--color-text-muted))] italic">Select a run to begin polling.</p>}

        {run && (
          <>
            <h3 className="text-tok-base font-semibold text-[rgb(var(--color-text))] mb-4">Run Status</h3>
            <dl className="grid grid-cols-2 gap-x-6 gap-y-2 text-tok-sm mb-4">
              <dt className="font-medium text-[rgb(var(--color-text-muted))]">Run ID</dt>
              {/* PATCH-S92-08: DDL PK is calculation_run_id */}
              <dd className="text-[rgb(var(--color-text))] font-mono text-tok-xs">{run.calculation_run_id || run.id || '—'}</dd>
              <dt className="font-medium text-[rgb(var(--color-text-muted))]">Run Type</dt>
              <dd className="text-[rgb(var(--color-text))]">{run.run_type}</dd>
              <dt className="font-medium text-[rgb(var(--color-text-muted))]">Status</dt>
              <dd className="text-[rgb(var(--color-text))]">{status}</dd>
              <dt className="font-medium text-[rgb(var(--color-text-muted))]">Locked</dt>
              <dd className="text-[rgb(var(--color-text))]">{run.is_locked ? 'Yes' : 'No'}</dd>
              <dt className="font-medium text-[rgb(var(--color-text-muted))]">Last Polled</dt>
              <dd className="text-[rgb(var(--color-text))]">{lastPolledAt ? lastPolledAt.toLocaleTimeString() : '—'}</dd>
              {/* OI-S892-PLAN-IDENTITY-ON-RUN-SURFACES: removed single-plan display.
                  Prism10k has 13 plans — showing one plan_code is misleading.
                  Plan identity belongs in a drill-down surface, not here. S1000. */}
              <dt className="font-medium text-[rgb(var(--color-text-muted))]">Positions Calculated</dt>
              <dd className="text-[rgb(var(--color-text))]">{run.positions_calculated ?? <span className="italic text-[rgb(var(--color-text-muted))]">—</span>}</dd>
              <dt className="font-medium text-[rgb(var(--color-text-muted))]">Positions in Scope</dt>
              <dd className="text-[rgb(var(--color-text))]">{run.positions_in_scope ?? <span className="italic text-[rgb(var(--color-text-muted))]">—</span>}</dd>
              {run.initiated_at && run.completed_at && (
                <>
                  <dt className="font-medium text-[rgb(var(--color-text-muted))]">Duration</dt>
                  <dd className="text-[rgb(var(--color-text))]">
                    {(() => {
                      const ms = new Date(run.completed_at) - new Date(run.initiated_at)
                      if (isNaN(ms) || ms < 0) return '—'
                      if (ms < 1000) return `${ms}ms`
                      if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`
                      const mins = Math.floor(ms / 60000)
                      const secs = Math.floor((ms % 60000) / 1000)
                      return `${mins}m ${secs}s`
                    })()}
                  </dd>
                </>
              )}
            </dl>

            {!isTerminal && <p className="text-tok-sm text-[rgb(var(--color-primary))] mb-4">Polling remains active every 3 seconds.</p>}
            {isTerminal && (
              <p className="text-tok-sm text-[rgb(var(--color-text-muted))] mb-4">Polling stopped because this run reached {status}.</p>
            )}

            <button
              type="button"
              onClick={goNext}
              disabled={status !== 'completed'}
              className="px-4 py-2 rounded bg-[rgb(var(--color-primary))] text-white text-tok-sm font-medium hover:bg-[rgb(var(--color-primary-hover))] disabled:opacity-50"
            >
              Continue to payout results
            </button>
          </>
        )}
      </section>
    </section>
  )
}
